import { InjectionToken } from '@angular/core';

/** proAV.Net.Core `MqttService.GetStatus()`. */
export interface MqttStatus {
    mode: 'Off' | 'Client' | 'EmbeddedBroker';
    connected: boolean;
    broker: string;
    clientId: string;
    connectedSinceUtc?: string | null;
    lastError?: string | null;
    embeddedBroker?: { port: number; running: boolean; clients: number } | null;
    subscriptions: string[];
}

/** proAV.Net.Core `MqttMonitoredMessage`. */
export interface MqttMessage {
    seq: number;
    timestampUtc: string;
    /** `out`: published by this processor; `in`: published by anything else. */
    direction: 'in' | 'out';
    topic: string;
    /** UTF-8 text, cut to the processor's limit (`truncated`). */
    payload: string;
    /** Full payload size in bytes. */
    size: number;
    truncated: boolean;
    retained: boolean;
    qos: number;
    /** The client that published it, when the processor runs the broker. */
    clientId?: string | null;
}

/** proAV.Net.Core `MqttMonitorSnapshot`. */
export interface MqttMonitorSnapshot {
    /** Pass back with the next read. Changes when capture restarts. */
    captureId: string;
    captureStartedUtc: string;
    /** The previous capture had stopped, so messages in between were not recorded. */
    restarted: boolean;
    /** Messages dropped because the processor's buffer filled before this read. */
    missed: number;
    /** More messages are waiting: read again straight away. */
    hasMore: boolean;
    capacity: number;
    idleTimeoutSeconds: number;
    messages: MqttMessage[];
}

export interface MqttMonitorResponse {
    /** False in the cloud portal for users who may only view the device: the connection shows, messages do not. */
    messagesAllowed: boolean;
    status: MqttStatus | null;
    /** With the embedded broker: the processor's LAN address, which devices connect to. */
    brokerAddress?: string | null;
    /** Null when MQTT is off, or messages are not allowed. */
    monitor: MqttMonitorSnapshot | null;
}

export const MQTT_SERVICE = new InjectionToken<IMqttService>('MQTT_SERVICE');

/**
 * The MQTT tool's data. Device mode talks to the processor's DashboardHub; cloud mode goes through
 * DeviceRegistry.Api and the device tunnel. Same seam as POWER_SERVICE and PING_SERVICE.
 */
export interface IMqttService {
    /** How often the page polls while messages are shown. */
    readonly pollIntervalMs: number;

    /** The connection, and the messages after `after` in capture `captureId` (null starts reading a capture from the beginning). */
    monitor(captureId: string | null, after: number): Promise<MqttMonitorResponse>;
}

/** True when `topic` matches the MQTT filter, honouring `+` and `#`. Mirrors proAV.Net.Core `MqttTopics.IsMatch`. */
export function topicMatches(filter: string, topic: string): boolean {
    if (!filter || !topic) return false;
    if (topic.startsWith('$') && !filter.startsWith('$')) return false;
    const f = filter.split('/');
    const t = topic.split('/');
    for (let i = 0; i < f.length; i++) {
        if (f[i] === '#') return i === f.length - 1;
        if (i >= t.length) return false;
        if (f[i] !== '+' && f[i] !== t[i]) return false;
    }
    return f.length === t.length;
}

/**
 * The page's filter box: an MQTT topic filter when it contains `+` or `#`, otherwise text to find in the topic,
 * payload or client id (any case).
 */
export function messageMatches(message: MqttMessage, filter: string): boolean {
    const text = filter.trim();
    if (!text) return true;
    if (text.includes('+') || text.includes('#')) return topicMatches(text, message.topic);
    const needle = text.toLowerCase();
    return message.topic.toLowerCase().includes(needle) || message.payload.toLowerCase().includes(needle) || (message.clientId ?? '').toLowerCase().includes(needle);
}

/** JSON payloads indented for reading; anything else as it came. */
export function formatPayload(message: MqttMessage): string {
    const text = message.payload;
    const start = text.trimStart()[0];
    if (!message.truncated && (start === '{' || start === '[')) {
        try {
            return JSON.stringify(JSON.parse(text), null, 2);
        } catch {
            // Not JSON after all.
        }
    }
    return text;
}

export interface MqttTopicGroup {
    /** The first topic level: for Shelly devices, the device id. */
    prefix: string;
    count: number;
    lastSeenUtc: string;
}

/** Messages grouped by first topic level, busiest first, so a quiet or chatty device stands out. */
export function groupByPrefix(messages: readonly MqttMessage[]): MqttTopicGroup[] {
    const groups = new Map<string, MqttTopicGroup>();
    for (const m of messages) {
        const prefix = m.topic.split('/')[0] || m.topic;
        const group = groups.get(prefix);
        if (!group) groups.set(prefix, { prefix, count: 1, lastSeenUtc: m.timestampUtc });
        else {
            group.count++;
            if (m.timestampUtc > group.lastSeenUtc) group.lastSeenUtc = m.timestampUtc;
        }
    }
    return [...groups.values()].sort((a, b) => b.count - a.count || a.prefix.localeCompare(b.prefix));
}
